import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import NavItem, { NavItemType } from './components/NavItem';
import TextNavItem from './components/TextNavItem';
import TextNavPopover from './components/TextNavPopover';
import SessionDetailBackButton from './SessionDetailBackButton';
import ClipListPopover from './ClipListPopover';
import HighlightReelPopover from './HighlightReelPopover';
import { NavigationEndpoint } from './types';
import { NavigationEndpointConfig } from './constants';
import UploadEditorReadinessBanner from './UploadEditorReadinessBanner';
import { EditorNavSlotProvider } from '@/context/EditorNavSlotContext/EditorNavSlotContext';
import { aiEditsStore } from '@/stores/aiEditsStore';
import { clearUnseenClipIds } from '@/stores/unseenClips';
import Loader from '@/components/atoms/Loader';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import AIDebugTools from '@/components/molecules/AIDebugTools/AIDebugTools';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { READINESS_DISMISS_EVENT } from '@/components/organisms/ContentReadinessPopup/constants';
import useSavedSearch from '@/hooks/useSavedSearch';
import ContentSettings from '@/components/molecules/ContentSettings';
import { useClipListPopover } from '@/hooks/useClipListPopover';
import { useHighlightReelPopover } from '@/hooks/useHighlightReelPopover';
import { isClipEditorPath } from '@/utils/clipEditorRoutes';
import { useRemoteApp } from '@/context/RemoteAppContext/RemoteAppContext';
import Icon from '@/components/atoms/Icon';
import { useUploadEditorReadiness } from '@/hooks/useUploadEditorReadiness';
import { useHighlightReelGenerationStatus } from '@/hooks/useHighlightReelGenerationStatus';
import useAIAddon from '@/hooks/useAIAddon';
import { isSimplifiedUploadEnabled } from '@/stores/clip';
import { UploadEditorReadinessContext } from '@/context/UploadEditorReadinessContext/UploadEditorReadinessContext';

export default function SessionDetailLayout({ isLoading, navMenu }: { isLoading: boolean; navMenu: NavItemType[] }) {
  const { isRemoteApp } = useRemoteApp();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const { isClipListPopoverOpen } = useClipListPopover();
  const { isHighlightReelPopoverOpen } = useHighlightReelPopover();
  const aiDebugToolsEnabled = featureFlags[FeatureFlagKeys.Use_CL_AI_Debug_Tools];
  const hasAIAddon = useAIAddon();
  // Highlight reels are LLM-generated, so they are gated on the AI add-on like text assets.
  const highlightReelEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Highlight_Reel] && hasAIAddon;
  const location = useLocation();
  const { isSavedSearchPage } = useSavedSearch();
  const { broadcastId } = useParams<{ broadcastId: string }>();
  const uploadEditorReadiness = useUploadEditorReadiness(broadcastId);
  useHighlightReelGenerationStatus(highlightReelEnabled ? broadcastId : undefined);
  const { clipsReady, layoutsReady } = uploadEditorReadiness;
  // Becomes true once ClipsContext finishes its initial load and signals back via context.
  // Keeps the banner hidden while the skeleton is still showing.
  const [editorLoaded, setEditorLoaded] = useState(false);

  // Dismiss any open readiness popup when the editor mounts — covers all entry flows.
  useEffect(() => {
    if (isSimplifiedUploadEnabled()) {
      document.dispatchEvent(new CustomEvent(READINESS_DISMISS_EVENT));
    }
  }, []);

  // DOM node of the full-width top-nav slot; the editor portals its ClipHeader into it.
  const [navSlotEl, setNavSlotEl] = useState<HTMLElement | null>(null);
  // Set by the editor when it mounts a top nav; the back button then lives there, not in the rail.
  const [hasEditorNav, setHasEditorNav] = useState(false);
  const navSlotValue = useMemo(() => ({ navSlotEl, setHasNav: setHasEditorNav }), [navSlotEl]);
  // The Co-edit sidebar is a fixed overlay on the right; shrink the top nav by its width so the
  // nav sits beside it (not underneath) — mirrors how the editor content reflows.
  const { isAIEditsSidebarOpen, sidebarWidth, isResizingSidebar } = useSyncExternalStore(
    aiEditsStore.subscribe,
    aiEditsStore.getSnapshot
  );

  // When clip generation finishes (clipsReady transitions false → true), refresh the clips list
  // so the menu isn't empty after the "AI clips are ready!" toast. The ref guards against firing
  // on mount when clipsReady is already true (content already processed).
  const prevClipsReadyRef = useRef(clipsReady);
  useEffect(() => {
    if (clipsReady && !prevClipsReadyRef.current) {
      EventBus.dispatch(CustomEvents.ReloadAllClips, {});
    }
    prevClipsReadyRef.current = clipsReady;
  }, [clipsReady]);

  useEffect(() => {
    if (isClipEditorPath(location.state?.previousPathname)) {
      clearUnseenClipIds();
    }
  }, [location.state?.previousPathname]);

  const openSettingsDrawer = useCallback(() => {
    EventBus.dispatch(CustomEvents.OpenContentSettings, { speakerId: null });
  }, []);

  // Keeps a nav button visually pressed while its popover is open.
  const showHoverEffect = useCallback(
    (label: string) => {
      if (isClipListPopoverOpen && NavigationEndpointConfig[NavigationEndpoint.clips].label === label) {
        return true;
      }
      if (isHighlightReelPopoverOpen && NavigationEndpointConfig[NavigationEndpoint.highlights].label === label) {
        return true;
      }
      return false;
    },
    [isClipListPopoverOpen, isHighlightReelPopoverOpen]
  );

  return (
    <UploadEditorReadinessContext.Provider value={{ ...uploadEditorReadiness, setEditorLoaded }}>
      <EditorNavSlotProvider value={navSlotValue}>
        <div className={`z-40 flex h-full flex-col bg-slate-50 ${isRemoteApp ? '' : 'fixed inset-0'}`}>
          {/* Full-width top-nav slot: the editor portals its ClipHeader here so the bar sits above
              the left rail. Collapses to zero height on pages that don't render a nav. */}
          <div
            ref={setNavSlotEl}
            className="shrink-0"
            style={{
              paddingRight: isAIEditsSidebarOpen ? `${sidebarWidth}px` : 0,
              transition: isResizingSidebar ? 'none' : 'padding-right 300ms cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          />
          {isSimplifiedUploadEnabled() && editorLoaded && <UploadEditorReadinessBanner layoutsReady={layoutsReady} />}
          <div className="flex min-h-0 grow gap-1">
            {aiDebugToolsEnabled && <AIDebugTools />}
            <div className="z-10 flex w-20 shrink-0 select-none flex-col items-center justify-between overflow-y-auto bg-slate-50 pt-[1.2rem] transition-all duration-[400ms]">
              <div className="flex flex-col space-y-2">
                {!hasEditorNav && <SessionDetailBackButton />}
                {navMenu.map(navItem => {
                  if (navItem.label === NavigationEndpointConfig[NavigationEndpoint.text].label) {
                    return <TextNavItem key={navItem.label} />;
                  }
                  return (
                    <NavItem
                      navItem={{
                        ...navItem,
                        iconContainerClassName: showHoverEffect(navItem.label) ? 'bg-slate-200' : ''
                      }}
                      key={navItem.label}
                    />
                  );
                })}
              </div>
              <div className="flex flex-col items-center border-t border-t-slate-300/70 pb-5 pt-[0.45rem]">
                {!isSavedSearchPage && (
                  <button
                    onClick={openSettingsDrawer}
                    data-testid="session-detail-settings-button"
                    className="group flex flex-col items-center text-slate-600 hover:text-black"
                  >
                    <div className="flex size-9 items-center justify-center rounded-xl group-hover:bg-slate-200">
                      <Icon name="IconSettings" className="size-5" />
                    </div>
                    <div className="mt-0.5 text-xs">Settings</div>
                  </button>
                )}
              </div>
            </div>
            <div className="h-full max-w-full grow">
              {isLoading ? (
                <div className="flex size-full items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <Outlet />
              )}
            </div>
          </div>
          {!isSavedSearchPage && <ContentSettings />}
          <ClipListPopover />
          {highlightReelEnabled && <HighlightReelPopover />}
          <TextNavPopover />
        </div>
      </EditorNavSlotProvider>
    </UploadEditorReadinessContext.Provider>
  );
}
